/**
 * Swamithra — an Expert's saved, unsent reply on a query or a vetting request.
 *
 * ONE PER REQUEST, SHARED BY EVERY EXPERT. Any Expert who opens the request
 * sees it and can continue it; only its latest save is kept. Any Expert reply
 * on the request ends it. A taxpayer never sees one: the field exists only on
 * the panel's detail payloads.
 *
 * NOT THE VETTING `draft`. That is the taxpayer's own document in a vetting
 * request; this is the Expert's unsent answer, which is why it travels as
 * `responseDraft`.
 *
 * THE VERSION IS THE CONCURRENCY CONTROL. A save quotes the version it loaded
 * (`baseVersion`, 0 for a new draft) and a send from the draft quotes it too
 * (`draftVersion`). If another Expert saved in between, the API answers 409
 * `draft_conflict` and nothing is overwritten.
 */

import { z } from "zod"

import { attachmentSchema } from "./attachment"
import { RICH_TEXT_MAX_LENGTH } from "./constants"

export const responseDraftSchema = z.object({
	version: z.number().int().positive(),
	bodyRich: z.string(),
	savedByExpertId: z.string(),
	savedByName: z.string().default(""),
	savedAt: z.string(),
	attachments: z.array(attachmentSchema).default([]),
})

export type ResponseDraft = z.output<typeof responseDraftSchema>

/** The detail payloads' field: the draft, or null when there is none. */
export const optionalResponseDraftSchema = responseDraftSchema
	.nullish()
	.transform(value => value ?? null)

/** One save of the composer. The text may be empty when files are attached. */
export const saveResponseDraftInputSchema = z.object({
	bodyRich: z.string().max(RICH_TEXT_MAX_LENGTH, "That draft is too long."),
	/** The version that was loaded, or 0 when starting a new draft. */
	baseVersion: z.number().int().nonnegative(),
	/** The loaded draft's files that stay. Any not listed are deleted. */
	keepAttachmentIds: z.array(z.string()).default([]),
})

export type SaveResponseDraftInput = z.input<typeof saveResponseDraftInputSchema>

/** A reply sent from a loaded draft: the version it came from and the files it keeps. */
export const sendFromDraftSchema = z.object({
	version: z.number().int().positive(),
	keepAttachmentIds: z.array(z.string()).default([]),
})

export type SendFromDraft = z.input<typeof sendFromDraftSchema>
